import {FC, useContext, useEffect, useRef, useState} from "react";
import {Box, BoxProps, Button, LinearProgress, Stack} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {IconContext} from "@buildwitt/shapeshift-identity";
import {GRID_CHECKBOX_SELECTION_COL_DEF, GridColumns} from "@mui/x-data-grid-pro";
import {IPaginatedResponse, IUserCourseEnrollmentStatus, IUserEntity} from "@buildwitt/shapeshift-types";
import {Add, Check, Undo} from "@mui/icons-material";
import {CustomToolbar} from "@/components/admin/bulkEnrollment/CustomToolbar";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {AdminBulkPlaylistEnrollmentModal} from "@/components/admin/bulkEnrollmentPlaylist/AdminBulkPlaylistEnrollmentModal";
import {AdminBulkPlaylistUnenrollModal} from "@/components/admin/bulkEnrollmentPlaylist/AdminBulkPlaylistUnenrollmentModal";
import {AdminBulkPlaylistCompletionModal} from "@/components/admin/bulkEnrollmentPlaylist/AdminBulkPlaylistCompletionModal";
import {AdminTable} from "@/components/common";
import {BulkActionStatus, useManageBulkActionStatusContext} from "@/contexts/manageBulkActionStatusContext";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import AdminBulkPlaylistEnrollmentConfirmationModal from "@/components/admin/bulkEnrollmentPlaylist/AdminBulkPlaylistEnrollmentConfirmationModal";

const useStyles = makeStyles()((theme) => ({
	contentBox: {
		display: "flex",
		flexDirection: "column",
		marginTop: theme.spacing(3),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderWidth: theme.borderWidth.small,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: theme.borderRadius.small,
		"& svg path": {
			fill: theme.palette.grey[500]
		}
	},
	enrollmentAdminTable: {
		padding: 0,
		border: 0
	},
	progressBar: {
		width:"100%"
	},
	progressBarRoot:{
		width:"100%",
		alignItems:"center",
		gap: theme.spacing(2)
	},
	enrollmentDataGrid: {
		padding: 0,
		border: 0
	}
}));

interface ICustomProps {
	playlistId: number
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

export const EnrollmentTab:FC<IProps> = ({playlistId, ...baseProps}) => {
	const {t} = useSSTranslation();
	const {classes} = useStyles();
	const {courseBulkEnrollment} = useDevCycleSdk();
	const [bulkEnrollModalOpen, setBulkEnrollModalOpen] = useState<boolean>(false);
	const [bulkUnEnrollModalOpen, setBulkUnEnrollModalOpen] = useState<boolean>(false);
	const [bulkCompletionModalOpen, setBulkCompletionModalOpen] = useState<boolean>(false);
	const [selected, setSelected] = useState<number[]>([]);
	const [{ownedCohort}] = useContext(PageBaseContext)
	const {checkIcon} = useContext(IconContext)
	const {playlistEnrollment: {playlistEnrollmentStatus, setPlaylistEnrollmentStatus, updatePlaylistEnrollmentSnackbarId}} = useManageBulkActionStatusContext();
	const [enrollmentStatusAtMounting, setEnrollmentStatusAtMounting] = useState(playlistEnrollmentStatus);
	const {processInProgressSnackbar, closeSnackbar} = useCustomSnackbar();
	const refreshDataGridRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		if (playlistEnrollmentStatus === BulkActionStatus.complete){
			setSelected([]);
			if (refreshDataGridRef.current && enrollmentStatusAtMounting !== BulkActionStatus.complete) {
				refreshDataGridRef.current();
			}

			setPlaylistEnrollmentStatus(BulkActionStatus.none);
			setEnrollmentStatusAtMounting(BulkActionStatus.none);
		}
	}, [playlistEnrollmentStatus]);

	useEffect(() => {
		const handlePlaylistEnrollmentsCompleted = (event: CustomEvent) => {
			const {inProgress} = event.detail;
			if (!inProgress) {
				// Clear selection when enrollment fails to start
				setSelected([]);
				setPlaylistEnrollmentStatus(BulkActionStatus.none);
				closeSnackbar(null);
				updatePlaylistEnrollmentSnackbarId(null);
			}
		};

		window.addEventListener("playlistEnrollmentsCompleted", handlePlaylistEnrollmentsCompleted as EventListener);
		return () => {
			window.removeEventListener("playlistEnrollmentsCompleted", handlePlaylistEnrollmentsCompleted as EventListener);
		};
	}, [setPlaylistEnrollmentStatus, closeSnackbar, updatePlaylistEnrollmentSnackbarId]);

	const handleLoadingChange = (isLoading: boolean) => {
		if (isLoading) {
			setPlaylistEnrollmentStatus(BulkActionStatus.inProgress);
			const snackbarId = processInProgressSnackbar(
				t("bulk_enrollment_progress_msg", "Enrolling users… Don't close or refresh this window."),
				true,
				true
			);
			updatePlaylistEnrollmentSnackbarId(snackbarId);
		}
	};	

	const headCells: GridColumns<IPaginatedResponse<IUserCourseEnrollmentStatus[]>["data"][0]> = ([
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string",
			disableColumnMenu: true
		},
		{
			field: "firstname",
			flex: 1,
			headerName: "First Name",
			type: "string"
		},
		{
			field: "lastname",
			flex: 1,
			headerName: "Last Name",
			type: "string"
		},
		{
			field: "email",
			flex: 1,
			headerName: "Email",
			type: "string"
		},
		{
			field: "jobCategory",
			flex: 1,
			headerName: "Job Category",
			type: "string"
		},
		{
			field: "jobTitle",
			flex: 1,
			headerName: "Job Title",
			type: "string"
		},
		{
			field: "hireDate",
			flex: 1,
			headerName: "Hire Date",
			type: "date",
			valueGetter: (params) => params.row.hireDate ? new Date(params.row.hireDate).toLocaleDateString("en-US", { timeZone: "UTC" }) : null
		},
		{
			field: "titleStartDate",
			flex: 1,
			headerName: "Title Start Date",
			type: "date",
			valueGetter: (params) => params.row.titleStartDate ? new Date(params.row.titleStartDate).toLocaleDateString("en-US", { timeZone: "UTC" }) : null
		},
		{
			field: "enrolled",
			flex: 1,
			type: "boolean",
			headerName: "Enrolled",
			renderCell: e => {
				if(e.value){
					return checkIcon
				}
				else {
					return <></>
				}
			}
		},
		{
			field: "progress",
			flex: 1,
			headerName: "Playlist Completion",
			type: "number",
			renderCell: e => {
				const {classes} = useStyles();
				return <Stack className={classes.progressBarRoot} direction="row"><LinearProgress className={classes.progressBar} variant="determinate" value={e.value} /><span>{`${e.value}%`}</span></Stack>
			}
		},
		{
			field: "division",
			flex: 1,
			type: "string",
			headerName: "Division"
		},
		{
			field: "supervisorName",
			flex: 1,
			type: "string",
			headerName: "Supervisor"
		}
	])

	const isEnrollmentInProgress = playlistEnrollmentStatus === BulkActionStatus.inProgress;

	const Toolbar= (): JSX.Element => (<CustomToolbar filterButton>
		<Button disabled={selected.length === 0 || (courseBulkEnrollment && isEnrollmentInProgress)} onClick={() => setBulkEnrollModalOpen(true)}><Add /> ENROLL</Button>
		<Button disabled={selected.length === 0} onClick={() => setBulkUnEnrollModalOpen(true)}><Undo /> UNENROLL</Button>
		<Button disabled={selected.length === 0} onClick={() => setBulkCompletionModalOpen(true)}><Check /> COMPLETE</Button>
	</CustomToolbar>)

	if(!ownedCohort)
		return <LinearProgress />

	return <Box {...baseProps}>
		<Box className={classes.contentBox}>
		<AdminTable<"user", IUserEntity>
				type="user"
				url={`/api/strapi-plugin-shapeshift-lms/cohort/${ownedCohort.id}/group/${playlistId}/users/enrollments`}
				initialState={{
					sorting: {
						sortModel: [{field: "assignedOn", sort: "desc"}]
					},
					columns: {
						columnVisibilityModel: {
							email: false,
							supervisorName: false,
							hireDate: false,
							titleStartDate: false
						}
					}
				}}
				className={classes.enrollmentDataGrid}
				checkboxSelection
				autoHeight
				aria-label="admin table"
				keepNonExistentRowsSelected
				pagination
				selectionModel={selected}
				onSelectionModelChange={e => setSelected(e as number[])}
				paginationMode="server"
				sortingMode="server"
				filterMode="server"
				columns={headCells as any}
				rowsPerPageOptions={[25, 50, 100, 200]}
				components={{Toolbar}}
				>
				{(_,reload) => {
					refreshDataGridRef.current = reload;

					return <>
						{courseBulkEnrollment ? (
							<AdminBulkPlaylistEnrollmentConfirmationModal
								playlistId={playlistId}
								selectedUserIds={selected}
								onClose={(r) => { setSelected([]); setBulkEnrollModalOpen(false); if(r) reload()}}
								open={bulkEnrollModalOpen}
								onLoadingChange={handleLoadingChange}
							/>
						) : (
							<AdminBulkPlaylistEnrollmentModal
								playlistId={playlistId}
								selected={selected}
								onClose={(r) => { setSelected([]); setBulkEnrollModalOpen(false); if(r) reload()}}
								open={bulkEnrollModalOpen}
							/>
						)}
						<AdminBulkPlaylistUnenrollModal playlistId={playlistId} onClose={(r) =>{ setSelected([]); setBulkUnEnrollModalOpen(false); if(r) reload()}} open={bulkUnEnrollModalOpen} selected={selected} />
						<AdminBulkPlaylistCompletionModal playlistId={playlistId} onClose={(r) =>{setSelected([]); setBulkCompletionModalOpen(false); if(r) reload()}} open={bulkCompletionModalOpen} selected={selected} />
					</>
				}}
			</AdminTable>
		</Box>
	</Box>
}